import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import { useDaumPostcode } from '@/lib/daumPostcode';
import {
  Button, PageShell, SectionCard, FormSection, Input, Switch, ImageUploadField,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import { useImageUploadQueue } from '@/lib/upload';
import { fetchNextStoreSortOrder, useOfflineStoreQuery, useOfflineStoreMutation } from '../hooks/useOfflineStoresQuery';
import type { OfflineStoreRequest } from '../types/cms';

const EMPTY: OfflineStoreRequest = {
  storeKey: '', name: '', address: '', hoursLabel: '', thumbnailUrl: '', href: '',
  isActive: true, sortOrder: 0,
};

// QA-56: 매장명/주소는 매장 식별의 핵심 필드(BE 비-nullable) → 필수 표기.
// QA-409: BE 는 운영 시간·썸네일도 @NotBlank 라 비우면 400 이었다 → 필수로 표기하고 저장 전에 막는다.
// 주소는 아래 별도 필드(주소 검색)로 받는다.
const FIELDS: { key: keyof OfflineStoreRequest; labelKey: string; placeholderKey?: string; required?: boolean }[] = [
  { key: 'name', labelKey: 'cms.stores.fields.name', required: true },
  { key: 'hoursLabel', labelKey: 'cms.stores.fields.hoursLabel', required: true },
  // M-3(ID99): 매장 카드/지도 아이콘 클릭 시 이동할 링크. 네이버/카카오/구글맵 URL 권장.
  { key: 'href', labelKey: 'cms.stores.fields.href', placeholderKey: 'cms.stores.fields.hrefPlaceholder' },
];

/** QA-409 — 주소는 [주소 검색]으로 고른 기본 주소 + 상세 주소를 한 줄로 저장한다(BE address 단일 문자열). */
export const joinStoreAddress = (base: string, detail: string): string =>
  [base.trim(), detail.trim()].filter(Boolean).join(' ');

export const OfflineStoreEditPage = () => {
  const { t } = useTranslation('common');
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  // QA-409 — '/cms/offline-stores/new' 는 :storeId 가 없는 별도 라우트라 storeId 가 undefined 다.
  //   종전엔 storeId === 'new' 만 신규로 봐서 등록 화면이 수정 모드(문구 '수정'·삭제 버튼)로 떴고,
  //   저장하면 PUT /offline-stores/NaN → 400 이었다.
  const isNew = !storeId || storeId === 'new';
  const id = isNew ? 0 : Number(storeId);

  const query = useOfflineStoreQuery(id);
  const { create, update, remove } = useOfflineStoreMutation();

  const [form, setForm] = useState<OfflineStoreRequest>(EMPTY);
  // M-3(ID99-②) 전화번호 — be-event 동결 계약(eb847e0: nullable, ≤40)에 배선 완료.
  // 별도 draft 상태로 보관하고 submit 시 form 에 합쳐 phoneNumber(빈값=null) 로 전송한다.
  const [phoneNumberDraft, setPhoneNumberDraft] = useState('');
  const [addressBase, setAddressBase] = useState('');
  const [addressDetail, setAddressDetail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const uploads = useImageUploadQueue();
  const postcode = useDaumPostcode(
    ({ address }) => {
      setAddressBase(address);
      setAddressDetail('');
    },
    () => setError(t('cms.stores.addressSearchFailed', { defaultValue: '주소 검색을 열지 못했습니다. 잠시 후 다시 시도해 주세요.' })),
  );

  useEffect(() => {
    if (query.data) {
      setForm({ ...query.data });
      // M-3(③): 응답 phoneNumber(nullable) 를 그대로 hydrate. null/미존재 → ''.
      setPhoneNumberDraft(query.data.phoneNumber ?? '');
      // 기존 주소는 한 줄 그대로 기본 주소에 둔다(다시 검색하면 표준 표기로 바뀐다).
      setAddressBase(query.data.address ?? '');
      setAddressDetail('');
    }
  }, [query.data]);

  const set = <K extends keyof OfflineStoreRequest>(key: K, val: OfflineStoreRequest[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    setError(null);
    const address = joinStoreAddress(addressBase, addressDetail);
    const missingMessage = (missing: string[]) =>
      t('cms.stores.requiredMissing', {
        fields: missing.join(', '),
        defaultValue: `필수 항목을 입력해 주세요: ${missing.join(', ')}`,
      });
    const missing = [
      !form.name.trim() && t('cms.stores.fields.name'),
      !address && t('cms.stores.fields.address'),
      !form.hoursLabel.trim() && t('cms.stores.fields.hoursLabel'),
    ].filter((v): v is string => typeof v === 'string');
    if (missing.length > 0) {
      setError(missingMessage(missing));
      return;
    }
    setSaving(true);
    try {
      const resolved = await uploads.flush();
      const thumbnailUrl = typeof resolved.thumbnailUrl === 'string' ? resolved.thumbnailUrl : '';
      // 새로 고른 이미지는 flush 뒤에야 URL 이 생겨 썸네일 필수 확인은 여기서 한다.
      if (!thumbnailUrl) {
        setError(missingMessage([t('cms.stores.fields.thumbnailUrl')]));
        return;
      }
      // M-3(③): be-event 동결 계약(eb847e0)에 phoneNumber(nullable, ≤40) 가 도착해 배선.
      // 빈값은 null 로 전송(BE nullable 계약). trim 으로 공백만 입력된 케이스도 null 처리.
      const body: OfflineStoreRequest = {
        ...form,
        name: form.name.trim(),
        hoursLabel: form.hoursLabel.trim(),
        address,
        thumbnailUrl,
        phoneNumber: phoneNumberDraft.trim() || null,
      };
      if (isNew) {
        // QA-409 — 정렬 순서는 입력받지 않고 등록 순 일련번호(기존 최대 + 10)로 저장.
        await create.mutateAsync({ ...body, sortOrder: await fetchNextStoreSortOrder() });
      } else {
        await update.mutateAsync({ id, body });
      }
      navigate('/cms/offline-stores');
    } catch (e) {
      setError(friendlyMessage(e, t('cms.stores.saveFailed', { defaultValue: '저장에 실패했습니다.' })));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(id);
      navigate('/cms/offline-stores');
    } catch (e) {
      setError(e instanceof Error ? e.message : '삭제 실패');
    }
  };

  const isBusy = saving || create.isPending || update.isPending || remove.isPending;
  // 수정 모드에서 기존 데이터 로드 전에는 저장을 막는다(하이드레이션 레이스). 막지 않으면
  // 폼이 EMPTY 기본값일 때 저장돼 stale 값이 영속된다. isLoading(v5: enabled+fetching)은
  // new 모드(enabled=false/idle)에선 false 라 안전하다.
  const isLoadingExisting = !isNew && query.isLoading;
  // QA-409 — 수정 화면 제목은 "매장 수정" 대신 매장명.
  const editTitle = query.data?.name || t('cms.stores.editTitle');

  const breadcrumb = [
    { label: 'CMS', href: '/cms/offline-stores' },
    { label: t('cms.stores.title'), href: '/cms/offline-stores' },
    { label: isNew ? t('cms.create') : editTitle },
  ];

  return (
    <PageShell
      title={isNew ? t('cms.stores.createTitle') : editTitle}
      breadcrumb={breadcrumb}
      data-testid="offline-store-edit-page"
      actions={
        <div className="flex gap-2">
          {!isNew && (
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={isBusy}>
              {t('cms.delete')}
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isBusy || uploads.isUploading || isLoadingExisting}
            data-testid="offline-store-save"
          >
            {uploads.isUploading
              ? '이미지 업로드 중…'
              : isLoadingExisting
                ? t('cms.loading', { defaultValue: '불러오는 중…' })
                : isNew
                  ? t('cms.stores.submitCreate', { defaultValue: '등록' })
                  : t('cms.save')}
          </Button>
        </div>
      }
    >
      {(error || uploads.error) && (
        <div
          role="alert"
          className="rounded-md p-3 text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          data-testid="offline-store-error"
        >
          {error ?? uploads.error}
        </div>
      )}

      <SectionCard title={t('cms.stores.sectionBasic')}>
        <div className="flex flex-col gap-3">
          {FIELDS.slice(0, 1).map(({ key, labelKey, required }) => (
            <FormSection key={key} label={t(labelKey)} density="compact" required={required ?? false}>
              <Input
                type="text"
                name={key}
                value={String(form[key])}
                onChange={(e) => set(key, e.target.value as OfflineStoreRequest[typeof key])}
              />
            </FormSection>
          ))}
          <FormSection label={t('cms.stores.fields.address')} density="compact" required>
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <Input
                  type="text"
                  name="addressBase"
                  value={addressBase}
                  readOnly
                  placeholder={t('cms.stores.fields.addressSearchPlaceholder', { defaultValue: '[주소 검색]으로 입력합니다' })}
                  onClick={() => void postcode.open()}
                  data-testid="offline-store-address-base"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void postcode.open()}
                  data-testid="offline-store-address-search"
                >
                  {t('cms.stores.addressSearch', { defaultValue: '주소 검색' })}
                </Button>
              </div>
              <Input
                type="text"
                name="addressDetail"
                value={addressDetail}
                placeholder={t('cms.stores.fields.addressDetailPlaceholder', { defaultValue: '상세 주소 (예: 지하 1층)' })}
                onChange={(e) => setAddressDetail(e.target.value)}
                data-testid="offline-store-address-detail"
              />
            </div>
          </FormSection>
          {FIELDS.slice(1).map(({ key, labelKey, placeholderKey, required }) => (
            <FormSection key={key} label={t(labelKey)} density="compact" required={required ?? false}>
              <Input
                type="text"
                name={key}
                value={String(form[key])}
                placeholder={placeholderKey ? t(placeholderKey) : undefined}
                onChange={(e) => set(key, e.target.value as OfflineStoreRequest[typeof key])}
              />
            </FormSection>
          ))}
          {/* M-3(ID99-②) 전화번호 — 선택 입력. be-event 계약(eb847e0)에 배선 완료(영속). */}
          <FormSection label={t('cms.stores.fields.phoneNumber')} density="compact">
            <Input
              type="tel"
              value={phoneNumberDraft}
              placeholder={t('cms.stores.fields.phoneNumberPlaceholder')}
              onChange={(e) => setPhoneNumberDraft(e.target.value)}
            />
          </FormSection>
          <FormSection label={t('cms.stores.fields.thumbnailUrl')} density="compact" required>
            <ImageUploadField
              queue={uploads}
              name="thumbnailUrl"
              category="OFFLINE_STORE"
              value={form.thumbnailUrl || null}
              onChange={(url) => set('thumbnailUrl', url ?? '')}
              disabled={isBusy}
            />
          </FormSection>
        </div>
      </SectionCard>

      <SectionCard title={t('cms.stores.sectionDisplay')}>
        <div className="flex flex-col gap-3">
          <FormSection label={t('cms.stores.fields.isActive')} density="compact">
            <Switch
              checked={form.isActive}
              onCheckedChange={(v) => set('isActive', v)}
              aria-label={t('cms.stores.fields.isActive')}
            />
          </FormSection>
        </div>
      </SectionCard>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cms.stores.title')} 삭제</DialogTitle>
            <DialogDescription>
              {t('cms.stores.confirmDelete', { name: form.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('cms.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={isBusy}
              onClick={() => { setConfirmDelete(false); void handleDelete(); }}
            >
              {t('cms.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
